import Phaser from 'phaser';
import SocketManager from '../network/SocketManager';
import { isAppRootPath, isNativeAppRuntime, parseAppRoute, setAppRoute } from '../router';
import { SFX_FINISH_KEY, SFX_POINTS_KEY, SFX_SPIN_WHEEL_KEY, SFX_TAP_KEY } from '../audio/sfx';
import logoUrl from '../../assets/logo.png';
import finishSfxUrl from '../../assets/sounds/finish.wav';
import pointsSfxUrl from '../../assets/sounds/points.wav';
import tapSfxUrl from '../../assets/sounds/tap.wav';
import spinWheelSfxUrl from '../../assets/sounds/spin_wheel.mp3';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    if (!this.cache.audio.exists(SFX_TAP_KEY)) {
      this.load.audio(SFX_TAP_KEY, tapSfxUrl);
    }
    if (!this.cache.audio.exists(SFX_SPIN_WHEEL_KEY)) {
      this.load.audio(SFX_SPIN_WHEEL_KEY, spinWheelSfxUrl);
    }
    if (!this.cache.audio.exists(SFX_POINTS_KEY)) {
      this.load.audio(SFX_POINTS_KEY, pointsSfxUrl);
    }
    if (!this.cache.audio.exists(SFX_FINISH_KEY)) {
      this.load.audio(SFX_FINISH_KEY, finishSfxUrl);
    }
  }

  create(): void {
    const initialRoute = parseAppRoute();
    const sm = SocketManager.getInstance();
    const socket = sm.getSocket();

    // Web root (/pickitup) starts fresh; native launches always use root and must preserve active games.
    if (!isNativeAppRuntime() && isAppRootPath() && sm.hasSession()) {
      sm.logoutAndResetConnection();
    }

    // Game-Deep-Link nur erlauben, wenn die gespeicherte Session exakt zu diesem Raum passt.
    if (initialRoute.kind === 'game') {
      const savedRoom = (sm.roomId || '').trim().toUpperCase();
      const matchesRequestedRoom = Boolean(sm.playerId && savedRoom && savedRoom === initialRoute.roomId);
      if (!matchesRequestedRoom && sm.hasSession()) {
        sm.logoutAndResetConnection();
      }
    }

    const uiLayer = document.getElementById('ui-layer');
    if (uiLayer) {
      uiLayer.innerHTML = `
        <div class="ui-bg-glow"></div>
        <div class="ui-bg-glow-2"></div>
        <div class="ui-panel" style="background: transparent; border: none; box-shadow: none; backdrop-filter: none;">
          <div id="boot-logo-shell" class="ui-boot-logo-shell">
            <div class="ui-boot-logo-inner">
              <img id="boot-logo-image" src="${logoUrl}" alt="PickItUp Logo" />
            </div>
            <div class="ui-boot-logo-shine"></div>
          </div>
          <h1 class="ui-boot-title">PICKITUP</h1>
          <div class="ui-boot-subtitle">MULTIPLAYER</div>
          <div class="ui-boot-divider"></div>
          <div id="boot-status" class="ui-boot-status">Verbinde...</div>
          <div id="boot-dot" class="ui-boot-dot"></div>
        </div>
      `;
    }

    const statusText = document.getElementById('boot-status');
    const dot = document.getElementById('boot-dot');
    let proceeded = false;

    const goOnce = () => {
      if (proceeded) return;
      proceeded = true;
      this.goToLobby();
    };

    const tryGo = () => {
      if (statusText) {
        statusText.innerText = 'Verbunden';
        statusText.style.color = 'var(--accent-green)';
      }
      if (dot) {
        dot.style.background = 'var(--accent-green)';
        dot.style.animation = 'none';
      }
      this.time.delayedCall(600, goOnce);
    };

    if (initialRoute.kind === 'gameOffline') {
      if (statusText) {
        statusText.innerText = 'Offline-Einzelspieler';
        statusText.style.color = 'var(--accent-gold)';
      }
      if (dot) {
        dot.style.background = 'var(--accent-gold)';
        dot.style.animation = 'none';
      }
      this.time.delayedCall(280, goOnce);
      return;
    }

    if (socket.connected) {
      tryGo();
    } else {
      socket.connect();
      socket.once('connect', tryGo);
      this.time.delayedCall(3_000, () => {
        if (!socket.connected && !proceeded) {
          socket.off('connect', tryGo);
          if (statusText) {
            statusText.innerText = 'Offline-Modus verfügbar';
            statusText.style.color = 'var(--accent-gold)';
          }
          if (dot) {
            dot.style.background = 'var(--accent-gold)';
            dot.style.animation = 'none';
          }
          this.time.delayedCall(650, goOnce);
        }
      });
    }
  }

  private goToLobby(): void {
    const uiLayer = document.getElementById('ui-layer');
    if (uiLayer) uiLayer.innerHTML = '';

    const sm = SocketManager.getInstance();
    const route = parseAppRoute();
    const sessionRoom = (sm.roomId || '').trim().toUpperCase();
    const hasSession = Boolean(sm.playerId && sessionRoom);

    if (route.kind === 'gameOffline') {
      const offlineName = (sm.playerName || 'Spieler').trim().slice(0, 20) || 'Spieler';
      try {
        const room = sm.startLocalSinglePlayer(offlineName);
        setAppRoute({ kind: 'gameOffline' }, { replace: true });
        this.scene.start('GameScene', { room });
      } catch {
        setAppRoute({ kind: 'home' }, { replace: true });
        this.scene.start('LobbyScene');
      }
      return;
    }

    if (route.kind === 'game') {
      if (hasSession && sessionRoom === route.roomId) {
        this.scene.start('GameScene');
      } else {
        setAppRoute({ kind: 'home' }, { replace: true });
        this.scene.start('LobbyScene');
      }
      return;
    }

    if (isAppRootPath()) {
      setAppRoute({ kind: 'home' }, { replace: true });
      this.scene.start('LobbyScene');
      return;
    }

    if (hasSession) {
      if (sm.isLocalMode()) {
        setAppRoute({ kind: 'gameOffline' }, { replace: true });
        this.scene.start('GameScene');
        return;
      }
      setAppRoute({ kind: 'game', roomId: sessionRoom }, { replace: true });
      this.scene.start('GameScene');
      return;
    }

    if (route.kind === 'unknown') {
      setAppRoute({ kind: 'home' }, { replace: true });
    } else if (route.kind === 'result') {
      setAppRoute({ kind: 'room', roomId: route.roomId }, { replace: true });
    } else if (route.kind === 'home' && isAppRootPath()) {
      setAppRoute({ kind: 'home' }, { replace: true });
    }
    this.scene.start('LobbyScene');
  }
}
